import React, { useEffect, useRef, useState } from "react";
import DigitControl from "./DigitControl";
import GroupedDigitControl from "./GroupedDigitControl";
import CheckboxListGroupControl from "./CheckboxListGroupControl";
import Tooltip from "./Tooltip/Tooltip";

/*
  Champ de formulaire universel. Même signature qu'avant (props ci-dessous) ;
  la refonte porte sur l'accessibilité :
  - libellé relié au champ par son identifiant, ou groupe <fieldset>/<legend>
    pour les listes de boutons et de cases ;
  - message d'erreur identifié (`<id>-error`, role="alert") et rattaché au champ
    (aria-describedby + aria-invalid) ;
  - champ obligatoire annoncé (aria-required), astérisque décoratif ;
  - boutons de choix avec aria-pressed ; résultats d'autocomplétion cliquables au
    clavier (boutons) et affichés via l'état React plutôt que le DOM.
*/

const RequiredMark = ({ required }) =>
  required ? (
    <span className="text-danger ml-1" aria-hidden="true">
      *
    </span>
  ) : null;

const ErrorText = ({ id, message, tag = "small" }) => {
  const Tag = tag;
  return (
    <Tag id={id} role="alert" className="form-text text-danger">
      {message || null}
    </Tag>
  );
};

// Icône « valide » à droite du champ (inchangée).
const ValidIcon = ({ show, bottom = 17 }) =>
  show ? (
    <i
      style={{ position: "absolute", right: 0, bottom }}
      className="fa fa-check text-success animated fadeInLeft faster"
      aria-hidden="true"
    />
  ) : null;

const Control = ({
  inputStyle = {},
  label = false,
  type,
  value,
  change,
  name,
  id = null,
  suffix = false,
  k = -1,
  error = false,
  disabled = false,
  checked = false,
  datas = false,
  dataIndex = "id",
  dataLabel = "name",
  dataLabel2 = false,
  className = "",
  itemClassName = "",
  required = false,
  inputClassName = "",
  dataLabelIndex = false,
  inputParams = { min: 1, max: 100, step: 1 },
  selectFirstLabel = "...",
  containerStyle = {},
  isValid = false,
  autocomplete = "on",
  margin = " my-5 ",
  placeholder = "",
  digitParams = [],
  btnInline = false,
  autoFocus = false,
  groups = [],
  allDefaultsText = "",
  // Identifiants d'éléments décrivant le champ (ajoutés à celui de l'erreur).
  describedBy = "",
  // Aide affichée sous le libellé/légende, rattachée au champ (aria-describedby).
  hint = null,
}) => {
  const [acResults, setAcResults] = useState([]);
  const [acOpen, setAcOpen] = useState(false);
  const acCloseTimer = useRef(null);

  // Identifiant du champ : nom indexé dans une liste (k), sinon id fourni, sinon nom.
  const fieldId = String(k !== -1 ? name + "-" + k : id ? id : name);
  const errorId = fieldId + "-error";
  const hintId = fieldId + "-hint";
  const errorMessage = error && error[name] ? error[name] : "";
  const describedByIds = [describedBy, hint ? hintId : "", errorMessage ? errorId : ""]
    .filter(Boolean)
    .join(" ");
  const ariaDescribedBy = describedByIds || undefined;
  const invalid = errorMessage ? "true" : undefined;
  const Hint = () =>
    hint ? (
      <div className="control-hint" id={hintId}>
        {hint}
      </div>
    ) : null;
  const ariaRequired = required ? "true" : undefined;
  const isDisabled = disabled ? "disabled" : false;

  const onChange = k !== -1 ? (e) => change(e, k) : change;
  const emit = (target) => (k !== -1 ? change({ target }, k) : change({ target }));

  const itemLabel = (data) =>
    dataLabelIndex ? data[dataLabel][dataLabelIndex] : data[dataLabel];

  useEffect(() => {
    if (type === "autocomplete") {
      const needle = (value || "").toLowerCase();
      setAcResults(
        (datas || []).filter((d) => d[dataLabel].toLowerCase().includes(needle))
      );
    }
  }, [value, type, datas, dataLabel]);

  useEffect(() => () => clearTimeout(acCloseTimer.current), []);

  const groupClass = "form-group " + className + " " + margin;

  if (type === "checkbox") {
    const switchId =
      k !== -1 ? "customSwitch" + name + "-" + k : "customSwitch" + name;
    return (
      <div
        style={containerStyle}
        className={"custom-control custom-switch " + className + " " + margin}
      >
        <input
          type="checkbox"
          className={"custom-control-input " + inputClassName}
          disabled={isDisabled}
          name={name}
          id={switchId}
          onChange={onChange}
          checked={checked ? "checked" : false}
          aria-required={ariaRequired}
        />
        <label className="custom-control-label" htmlFor={switchId}>
          {label}
          <RequiredMark required={required} />
        </label>
      </div>
    );
  }

  if (type === "select") {
    return (
      <div style={containerStyle} className={groupClass}>
        {label && (
          <label htmlFor={fieldId}>
            {label}
            <RequiredMark required={required} />
          </label>
        )}
        <Hint />
        <div className="input-group">
          <select
            className={"form-control " + inputClassName}
            disabled={isDisabled}
            value={value ? value : ""}
            name={name}
            id={fieldId}
            onChange={onChange}
            aria-describedby={ariaDescribedBy}
            aria-invalid={invalid}
            aria-required={ariaRequired}
          >
            <option value="">{selectFirstLabel}</option>
            {datas.map((data, dk) => (
              <option
                key={name + "-" + (data.id ? data.id : dk)}
                value={data[dataIndex]}
              >
                {itemLabel(data)} {dataLabel2 ? " - " + data[dataLabel2] : ""}
              </option>
            ))}
          </select>
          {suffix && <div className="input-group-append">{suffix}</div>}
        </div>
        <ErrorText id={errorId} message={errorMessage} />
      </div>
    );
  }

  if (type === "autocomplete") {
    const openResults = () => {
      clearTimeout(acCloseTimer.current);
      setAcOpen(true);
    };
    const closeResults = () => {
      // Laisse le temps au clic sur un résultat d'être pris en compte.
      acCloseTimer.current = setTimeout(() => setAcOpen(false), 200);
    };
    return (
      <div style={containerStyle} className={groupClass}>
        {label && (
          <label htmlFor={fieldId}>
            {label}
            <RequiredMark required={required} />
          </label>
        )}
        <Hint />
        <div className="input-group">
          <input
            className={"form-control " + inputClassName}
            disabled={isDisabled}
            value={value ? value : ""}
            name={name}
            id={fieldId}
            onChange={onChange}
            onFocus={openResults}
            onBlur={closeResults}
            placeholder={placeholder}
            autoComplete={autocomplete}
            aria-describedby={ariaDescribedBy}
            aria-invalid={invalid}
            aria-required={ariaRequired}
            role="combobox"
            aria-autocomplete="list"
            aria-controls={"acres" + fieldId}
            aria-expanded={acOpen ? "true" : "false"}
          />
          <ValidIcon show={isValid} />
          <div
            className="ac-results position-relative"
            id={"acres" + fieldId}
            style={{
              top: -5,
              padding: 5,
              width: "100%",
              zIndex: "999",
              display: acOpen ? "block" : "none",
            }}
          >
            <p className="my-1 text-primary">Sélectionnez une option ci-dessous</p>
            {acResults.slice(0, 5).map((a, ak) => (
              <button
                type="button"
                className="ac-result animated fadeInUp faster btn btn-block text-left"
                style={{
                  padding: "8px 5px",
                  width: "100%",
                  cursor: "pointer",
                  border: "1px solid #dddddd",
                  animationDelay: ak * 50 + "ms",
                }}
                onFocus={openResults}
                onBlur={closeResults}
                onClick={() => {
                  clearTimeout(acCloseTimer.current);
                  setAcOpen(false);
                  emit({ name: name, value: a[dataIndex] });
                }}
                key={"acres" + ak + "" + k}
              >
                {a[dataLabel]} {dataLabel2 ? " - " + a[dataLabel2] : ""}
              </button>
            ))}
          </div>
          {suffix && <div className="input-group-append">{suffix}</div>}
        </div>
        <ErrorText id={errorId} message={errorMessage} />
      </div>
    );
  }

  if (type === "textarea") {
    return (
      <div style={containerStyle} className={groupClass}>
        {label && (
          <label htmlFor={fieldId}>
            {label}
            <RequiredMark required={required} />
          </label>
        )}
        <Hint />
        <div className="input-group">
          <textarea
            className={"form-control " + inputClassName}
            disabled={isDisabled}
            id={fieldId}
            name={name}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            aria-describedby={ariaDescribedBy}
            aria-invalid={invalid}
            aria-required={ariaRequired}
          />
          {suffix && (
            <div className="input-group-append">
              <span
                className="input-group-text"
                style={disabled ? { color: "#9a9a9a" } : {}}
              >
                {suffix}
              </span>
            </div>
          )}
        </div>
        <ErrorText id={errorId} message={errorMessage} />
      </div>
    );
  }

  if (type === "btnList" || type === "checkBtnList") {
    const multiple = type === "checkBtnList";
    const isSelected = (data) =>
      multiple
        ? Array.isArray(value) && value.includes(data[dataIndex])
        : value == data[dataIndex]; // eslint-disable-line eqeqeq
    return (
      <fieldset
        style={containerStyle}
        className={groupClass}
        aria-describedby={ariaDescribedBy}
        aria-invalid={invalid}
        aria-required={ariaRequired}
      >
        <legend className="control-legend d-block">
          {label}
          <RequiredMark required={required} />
        </legend>
        <Hint />
        <div
          className={
            "btn-group " +
            (multiple && btnInline ? "d-flex flex-row flex-wrap " : "")
          }
        >
          {datas.map((data, dk) => (
            <div
              className="d-flex align-items-center"
              style={{ gap: 10 }}
              key={
                name +
                "-" +
                (data.id ? data.id : data._id ? data._id + dk : "abcd" + dk)
              }
            >
              <button
                className={
                  itemClassName +
                  " btn-input btn btn-default " +
                  (isSelected(data) ? "active " : " ") +
                  (multiple && btnInline ? "text-center btn-sm" : "")
                }
                type="button"
                aria-pressed={isSelected(data) ? "true" : "false"}
                onClick={() =>
                  multiple
                    ? emit({
                        name: name,
                        value: data[dataIndex],
                        // Même forme d'événement qu'une case à cocher : coché = ajouté.
                        formArray: { target: { checked: !isSelected(data) } },
                      })
                    : emit({ name: name, value: data[dataIndex] })
                }
              >
                <p style={{ width: "93%", margin: 0 }}>{itemLabel(data)}</p>
                {isSelected(data) && !(multiple && btnInline) && (
                  <i
                    className="fa fa-check ml-2 animated fadeInRight faster"
                    aria-hidden="true"
                  />
                )}
              </button>
              {data.explication ? (
                <Tooltip text={data.explication}>
                  <></>
                </Tooltip>
              ) : (
                <div style={{ width: 18 }}></div>
              )}
            </div>
          ))}
        </div>
        <ErrorText id={errorId} message={errorMessage} />
      </fieldset>
    );
  }

  if (type === "checkboxList") {
    return (
      <fieldset
        style={containerStyle}
        className={groupClass}
        aria-describedby={ariaDescribedBy}
        aria-invalid={invalid}
        aria-required={ariaRequired}
      >
        <legend className="control-legend d-block">
          {label}
          <RequiredMark required={required} />
        </legend>
        <Hint />
        <div className="btn-group">
          {datas.map((data, dk) => {
            const switchId =
              "customSwitch" + name + "-" + (data[dataIndex] ?? dk) + (k !== -1 ? "-" + k : "");
            return (
              <div className="my-3 custom-control custom-switch" key={switchId}>
                <input
                  type="checkbox"
                  className={"custom-control-input " + inputClassName}
                  disabled={isDisabled}
                  name={name}
                  id={switchId}
                  onChange={(e) =>
                    emit({
                      name: name,
                      value: data[dataIndex],
                      formArray: e,
                      isExclusive: data.isExclusive,
                    })
                  }
                  checked={value.includes(data[dataIndex]) ? "checked" : false}
                />
                <label className="custom-control-label" htmlFor={switchId}>
                  {itemLabel(data)}
                </label>
              </div>
            );
          })}
        </div>
        <ErrorText id={errorId} message={errorMessage} />
      </fieldset>
    );
  }

  if (type === "range") {
    return (
      <div style={containerStyle} className={groupClass}>
        {label && (
          <label htmlFor={fieldId}>
            {label}
            <RequiredMark required={required} />
          </label>
        )}
        <Hint />
        <div className="input-group">
          <input
            min={inputParams.min}
            max={inputParams.max}
            step={inputParams.step}
            className={"form-control custom-range " + inputClassName}
            type="range"
            disabled={isDisabled}
            id={fieldId}
            name={name}
            value={value}
            onChange={onChange}
            aria-describedby={ariaDescribedBy}
            aria-invalid={invalid}
          />
          {suffix && (
            <div className="input-group-append">
              <span
                className="input-group-text"
                style={disabled ? { color: "#9a9a9a" } : {}}
              >
                {suffix}
              </span>
            </div>
          )}
        </div>
        <ErrorText id={errorId} message={errorMessage} />
        <input
          type="text"
          id={fieldId + "-value"}
          aria-label={(label ? label + " : " : "") + "valeur saisie"}
          className="form-control range-inp-value"
          onChange={onChange}
          name={name}
          value={value}
          disabled={isDisabled}
          placeholder={placeholder}
        />
      </div>
    );
  }

  if (type === "digits") {
    return (
      <div style={containerStyle} className={groupClass}>
        {label && (
          <label htmlFor={fieldId}>
            {label}
            <RequiredMark required={required} />
          </label>
        )}
        <Hint />
        <div className="input-group">
          <DigitControl
            params={digitParams}
            name={name}
            complete={onChange}
            value={value}
          />
          <ValidIcon show={isValid} bottom={51} />
        </div>
        <ErrorText id={errorId} message={errorMessage} />
      </div>
    );
  }

  if (type === "numberInt") {
    return (
      <div style={containerStyle} className={groupClass}>
        {label && (
          <label htmlFor={fieldId}>
            {label}
            <RequiredMark required={required} />
          </label>
        )}
        <Hint />
        <div className="input-group">
          <div className="input-group-prepend">
            <button
              type="button"
              className="btn btn-light shadow-none"
              aria-label="Diminuer"
              onClick={() => emit({ name: name, value: value - 1 })}
            >
              -
            </button>
          </div>
          <input
            autoComplete={autocomplete}
            style={inputStyle}
            className={"form-control text-center " + inputClassName}
            type={type}
            disabled={isDisabled}
            id={fieldId}
            name={name}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            aria-describedby={ariaDescribedBy}
            aria-invalid={invalid}
            aria-required={ariaRequired}
          />
          <ValidIcon show={isValid} />
          <div className="input-group-append">
            <button
              type="button"
              className="btn btn-light shadow-none"
              aria-label="Augmenter"
              onClick={() => emit({ name: name, value: value + 1 })}
            >
              +
            </button>
          </div>
        </div>
        <ErrorText id={errorId} message={errorMessage} />
      </div>
    );
  }

  if (type === "date") {
    return (
      <div style={containerStyle} className={groupClass}>
        {label && (
          <label htmlFor={fieldId}>
            {label}
            <RequiredMark required={required} />
          </label>
        )}
        <Hint />
        <div className="input-group">
          <GroupedDigitControl
            id={fieldId}
            name={name}
            value={value}
            change={change}
            separation={[2, 2, 4]}
            separator={"/"}
            type="date"
            placeholder="Entrez uniquement les chiffres ex : 01011980"
            inputProps={{
              "aria-describedby": ariaDescribedBy,
              "aria-invalid": invalid,
              "aria-required": ariaRequired,
              inputMode: "numeric",
            }}
          />
          <ValidIcon show={isValid} />
          {suffix && <div className="input-group-append">{suffix}</div>}
        </div>
        <ErrorText id={errorId} message={errorMessage} />
      </div>
    );
  }

  if (type === "checkboxListGroup") {
    /*
      Liste d'accordéons : chaque groupe propose une liste de cases dont une
      seule peut être retenue. Les groupes sont décrits par `groups`
      ({ id, label }) et chaque réponse porte l'identifiant de son groupe.
    */
    return (
      <CheckboxListGroupControl
        label={label}
        value={value}
        change={change}
        name={name}
        k={k}
        error={error}
        disabled={disabled}
        datas={datas}
        dataIndex={dataIndex}
        dataLabel={dataLabel}
        className={className}
        required={required}
        inputClassName={inputClassName}
        dataLabelIndex={dataLabelIndex}
        containerStyle={containerStyle}
        margin={margin}
        groups={groups}
        allDefaultsText={allDefaultsText}
        errorId={errorId}
        hint={hint}
        hintId={hintId}
        describedBy={describedBy}
      />
    );
  }

  // Champ simple : text, password, email, number…
  return (
    <div style={containerStyle} className={groupClass}>
      {label && (
        <label htmlFor={fieldId}>
          {label}
          <RequiredMark required={required} />
        </label>
      )}
      <div className="input-group">
        <input
          autoFocus={autoFocus}
          autoComplete={autocomplete}
          style={inputStyle}
          className={"form-control " + inputClassName}
          type={type}
          disabled={isDisabled}
          id={fieldId}
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          aria-describedby={ariaDescribedBy}
          aria-invalid={invalid}
          aria-required={ariaRequired}
        />
        <ValidIcon show={isValid} />
        {suffix && <div className="input-group-append">{suffix}</div>}
      </div>
      <ErrorText id={errorId} message={errorMessage} />
    </div>
  );
};

export default Control;
